import { formatAuditDateTime } from '../../../lib/auditColumnDefs';
import { GRID_LIST_FETCH_SIZE } from '../../../components/shared/agGridCells';
import { exportRowsToExcel } from '../../../utils/excelExport';
import { ACCENT_COLORS, AUDIT_LOG_EXPORT_COLUMNS, EMPTY_AUDIT_LOG_COLUMN_FILTERS } from '../constants/constants';
import type {
  AuditLogColumnFilterKey,
  AuditLogColumnFilters,
  AuditLogListRequest,
  AuditLogRow,
} from '../models/types';

const AUDIT_LOG_COLUMN_FILTER_KEYS: Record<AuditLogColumnFilterKey, true> = {
  userName: true,
  userType: true,
  action: true,
  entityName: true,
  recordId: true,
  ipAddress: true,
  createdAt: true,
};

function asString(value: unknown): string {
  if (value == null) return '';
  const text = String(value).trim();
  return text;
}

function stringifyUnknown(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function asJsonString(value: unknown): string {
  const text = stringifyUnknown(value);
  if (!text || text === '{}' || text === '[]' || text === 'null') return '';
  return text;
}

function hashAccent(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return ACCENT_COLORS[Math.abs(hash) % ACCENT_COLORS.length];
}

export function mapAuditLogRow(item: Record<string, unknown>): AuditLogRow {
  const userName = asString(item.userName);
  const createdAt = asString(item.createdAt) || null;
  const id =
    asString(item.id) ||
    [createdAt, userName, asString(item.action)].filter(Boolean).join('-') ||
    'log';

  return {
    id,
    userId: asString(item.userId),
    userName,
    userType: asString(item.userType),
    merchantId: asString(item.merchantId),
    action: asString(item.action) || '—',
    entityName: asString(item.entityName) || '—',
    recordId: asString(item.recordId),
    oldValue: asJsonString(item.oldValue),
    newValue: asJsonString(item.newValue),
    description: asString(item.description),
    ipAddress: asString(item.ipAddress) || '—',
    createdAt,
    accent: hashAccent(userName || id),
    raw: item,
  };
}

export function getApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const message = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
    if (message) return message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export async function exportAuditLogsExcel(rows: AuditLogRow[]) {
  await exportRowsToExcel(
    rows.map((row) => ({
      userName: row.userName || '—',
      userType: row.userType || '—',
      action: row.action,
      entityName: row.entityName,
      recordId: row.recordId || '—',
      description: row.description || '—',
      ipAddress: row.ipAddress,
      createdAt: formatAuditDateTime(row.createdAt),
    })),
    AUDIT_LOG_EXPORT_COLUMNS,
    {
      sheetName: 'Audit Logs',
      fileNamePrefix: 'audit-logs',
    },
  );
}

type AgGridSimpleFilterModel = {
  filter?: unknown;
  dateFrom?: string;
  dateTo?: string;
  conditions?: Array<{
    filter?: unknown;
    dateFrom?: string;
    dateTo?: string;
  }>;
};

function firstCondition(model: AgGridSimpleFilterModel): AgGridSimpleFilterModel {
  return model.conditions?.[0] ?? model;
}

export function mapAgGridFilterModelToAuditLogFilters(
  filterModel: Record<string, unknown> | null | undefined,
): Partial<AuditLogColumnFilters> {
  const columnFilters: Partial<AuditLogColumnFilters> = {};
  if (!filterModel) return columnFilters;

  for (const [colId, raw] of Object.entries(filterModel)) {
    if (!raw || typeof raw !== 'object') continue;
    const model = firstCondition(raw as AgGridSimpleFilterModel);

    if (colId === 'createdAt') {
      if (typeof model.dateFrom === 'string' && model.dateFrom.length >= 10) {
        const from = model.dateFrom.slice(0, 10);
        const to = (model.dateTo ?? model.dateFrom).slice(0, 10);
        columnFilters.createdAt = [from, to];
      }
      continue;
    }

    if (typeof model.filter !== 'string' || !model.filter.trim()) continue;
    if (colId in AUDIT_LOG_COLUMN_FILTER_KEYS) {
      columnFilters[colId as AuditLogColumnFilterKey] = [model.filter];
    }
  }

  return columnFilters;
}

export function buildAuditLogRequest(options: {
  page?: number;
  size?: number;
  filterStatus: string;
  search: string;
  columnFilters: AuditLogColumnFilters;
  export?: boolean;
}): AuditLogListRequest {
  const request: AuditLogListRequest = {
    page: options.page ?? 0,
    size: options.size ?? GRID_LIST_FETCH_SIZE,
  };

  if (options.filterStatus !== 'All') {
    request.status = options.filterStatus.toUpperCase();
  }

  const query = options.search.trim();
  if (query) request.search = query;

  const filters = Object.fromEntries(
    (Object.entries(options.columnFilters) as [AuditLogColumnFilterKey, string[] | null][])
      .filter(([, values]) => Boolean(values?.length))
      .map(([key, values]) => [key, values as string[]]),
  );

  if (Object.keys(filters).length > 0) request.filters = filters;
  if (options.export) request.export = true;

  return request;
}

export { EMPTY_AUDIT_LOG_COLUMN_FILTERS };
